import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';

import { GadgetMenuButtons } from 'components/Chart';
import BlockMenuItems, {
  hasBlockMenuItems,
} from 'components/Studio/BlockMenuItems';
import { getValueFontSize } from 'components/Studio/constants';
import FullscreenOverlay, {
  FULLSCREEN_LAYOUT,
  getFullscreenValueFontSize,
} from 'components/Studio/FullscreenOverlay';
import StudioCard from 'components/Studio/StudioCard';
import useElementSize from 'components/Studio/useElementSize';
import { toViewDateSettings } from 'contextProviders/DashboardGadgetDateScopeProvider';
import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';

import EditStudioBlockOption from './EditStudioBlockOption';
import FullscreenBlockButton from './FullscreenBlockButton';
import GadgetDateButton from './GadgetDateButton';

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  height: 100%;
`;

// A gadget Studio saved, drawn as Studio draws it: its header (icon, accent,
// or none), a Number's subtext, ratio and progress bar, a chart's forecast.
// Sits inside the gadget's usual providers, which already scope it to the
// gadget's dates and filters (and the dashboard's or report's), so it adds
// no scopes of its own. Keeps the classic gadget's menu buttons, with Studio
// added to its editors and its filters as an icon (where the account has
// Studio; otherwise the classic gadget's editors). On a dashboard, a classic
// gadget's date range for the view opens from a calendar icon beside them (a
// report gadget follows the report's dates). A fullscreen icon opens it,
// view-only, over the whole window.
const StudioGadgetBody = ({
  studioCard,
  chartDefinition,
  layout,
}: {
  studioCard: Studio.GadgetSidecarCard;
  chartDefinition: VisualisationDefinition;
  layout: CardLayout;
}) => {
  const { ref, width, height } = useElementSize<HTMLDivElement>();
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { i, x, y, w, h } = layout;
  const savedDateSettings = useMemo(
    () => (dashboardGadget ? toViewDateSettings(dashboardGadget) : undefined),
    [dashboardGadget],
  );
  const card = useMemo(
    (): Studio.Card => ({ id: i, ...studioCard, layout: { x, y, w, h } }),
    [h, i, studioCard, w, x, y],
  );
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  // Without Studio (see useCanOpenStudio), it's edited as a classic gadget:
  // the classic Edit and Edit Filters, rather than its Studio settings
  const canOpenStudio = useCanOpenStudio();

  return (
    <Wrapper ref={ref} data-testid={`studio-gadget-${i}`}>
      <StudioCard
        card={card}
        isSelected={false}
        isDropTarget={false}
        valueFontSize={getValueFontSize({ width, height })}
        viewMenu={
          <GadgetMenuButtons
            chartDefinition={chartDefinition}
            editOptions={canOpenStudio ? <EditStudioBlockOption /> : undefined}
            datesButton={
              dashboardGadget ? (
                <GadgetDateButton
                  savedDateSettings={savedDateSettings}
                  isBonusPeriodMode={dashboardGadget.isBonusPeriodMode}
                />
              ) : undefined
            }
            fullscreenButton={
              <FullscreenBlockButton onClick={() => setIsFullscreen(true)} />
            }
            // Named as the gadget is (readStudioBlock)
            extraMenuItems={
              hasBlockMenuItems({ content: card, isOnView: true }) ? (
                <BlockMenuItems
                  content={card}
                  name={chartDefinition.name}
                  w={card.layout.w}
                  h={card.layout.h}
                />
              ) : undefined
            }
          />
        }
        isEmbedded
      />
      {isFullscreen && (
        <FullscreenOverlay onClose={() => setIsFullscreen(false)}>
          <StudioCard
            card={{ ...card, layout: FULLSCREEN_LAYOUT }}
            isSelected={false}
            isDropTarget={false}
            valueFontSize={getFullscreenValueFontSize()}
            isEmbedded
          />
        </FullscreenOverlay>
      )}
    </Wrapper>
  );
};

export default StudioGadgetBody;
